<template>
  <div class="standard-container">
    <aside class="nav-sidebar">
      <TreeNav :nodes="standardNodes" />
    </aside>

    <main class="content-main">
      <header class="standard-header">
        <h1>{{ standard.standardName }}</h1>
        <el-form :model="standard" class="readonly-form" disabled>
          <el-form-item label="标准名称" prop="standardName">
            <el-input
              v-model="standard.standardName"
              placeholder="请输入标准名称"
            />
          </el-form-item>
          <el-form-item label="标准编号" prop="standardNumber">
            <el-input
              v-model="standard.standardNumber"
              placeholder="请输入标准号"
            />
          </el-form-item>
          <el-form-item label="归口单位" prop="responsibleUnit">
            <el-input
              v-model="standard.responsibleUnit"
              placeholder="请输入归口单位"
            />
          </el-form-item>
          <el-form-item label="发布部门" prop="departmentName">
            <el-input
              v-model="standard.departmentName"
              placeholder="请输入发布部门名称"
            />
          </el-form-item>
          <el-form-item label="起草人" prop="draftAuthor">
            <el-input
              type="textarea"
              v-model="standard.draftAuthor"
              placeholder="请输入起草人"
            />
          </el-form-item>
          <el-form-item label="起草单位" prop="draftUnit">
            <el-input
              type="textarea"
              v-model="standard.draftUnit"
              placeholder="请输入起草单位"
            />
          </el-form-item>
          <el-form-item label="适用范围" prop="scope">
            <el-input
              v-model="standard.scope"
              type="textarea"
              rows="3"
              placeholder="请输入适用范围"
            />
          </el-form-item>
          <el-form-item label="标准级别" prop="level">
            <el-select v-model="standard.level" placeholder="请选择级别">
              <el-option label="国家级" value="国家级" />
              <el-option label="省级" value="省级" />
              <el-option label="市级" value="市级" />
              <el-option label="企业级" value="企业级" />
            </el-select>
          </el-form-item>

          <el-form-item label="规范性引用文件">
            <el-input
              v-model="standard.normativeDocuments"
              type="textarea"
              rows="3"
              placeholder="请输入规范性引用文件内容"
            />
          </el-form-item>
          <el-form-item label="发布日期">
            <el-date-picker
              v-model="standard.publicationDate"
              type="datetime"
            />
          </el-form-item>
          <el-form-item label="实施日期">
            <el-date-picker
              v-model="standard.implementationDate"
              type="datetime"
            />
          </el-form-item>
          <el-form-item label="相关图片">
            <div class="image-preview-list" v-if="standard.imageList.length">
              <el-image
                v-for="(url, index) in standard.imageList"
                :key="index"
                style="width: 100px; height: 100px; margin-right: 10px"
                :src="url"
                :preview-src-list="standard.imageList"
                fit="cover"
                hide-on-click-modal
              >
                <template #error>
                  <div class="image-error">加载失败</div>
                </template>
              </el-image>
            </div>
          </el-form-item>
          <el-form-item label="相关文件">
            <div v-if="standard.file" class="file-preview">
              <el-link
                type="primary"
                :href="standard.file"
                target="_blank"
                :underline="false"
                class="file-link"
              >
                <el-icon><Document /></el-icon>
                {{ standard.fileName || "下载文件" }}
              </el-link>
            </div>
            <div v-else class="empty-file-tip">暂无关联文件</div>
          </el-form-item>
        </el-form>
      </header>

      <!-- 术语展示 -->
      <section v-if="terms.length" class="terms-section">
        <h2>术语和定义</h2>
        <div v-for="term in terms" :key="term.id" class="term-item">
          <h3>{{ term.cName }}（{{ term.eName }}）</h3>
          <p v-if="term.explanation">{{ term.explanation }}</p>
          <div class="image-preview-list" v-if="term.imageList?.length">
            <el-image
              v-for="(url, index) in term.imageList"
              :key="index"
              style="width: 100px; height: 100px; margin-right: 10px"
              :src="url"
              :preview-src-list="term.imageList"
              fit="cover"
              hide-on-click-modal
            >
              <template #error>
                <div class="image-error">加载失败</div>
              </template>
            </el-image>
          </div>
        </div>
      </section>

      <template v-if="loading">
        <el-skeleton :rows="6" animated />
      </template>
      <template v-else-if="error">
        <el-empty description="加载失败，请重试">
          <el-button @click="loadData">重新加载</el-button>
        </el-empty>
      </template>
      <template v-else>
        <ContentRenderer :nodes="standardNodes" :standard="standard" />
      </template>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { normalizeStandardData } from "@/stores/standard";
import TreeNav from "@/content/TreeNav.vue";
import { getStandardById } from "@/api/menu";
import ContentRenderer from "@/content/ContentRenderer.vue";

const props = defineProps<{
  standardData: any;
}>();

interface StandardDetail {
  standard: any;
  file: String;
  fileName: String;
  terms: Term[];
  level1: any[];
}
const route = useRoute();
const loading = ref(true);
const error = ref(false);
const terms = ref<any[]>([]);

interface Term {
  id: number;
  cName: string;
  eName: string;
  explanation?: string;
  image?: string;
  imageList?: string[];
}

const standard = ref({
  standardName: "",
  standardNumber: "",
  responsibleUnit: "",
  departmentName: "",
  draftAuthor: "",
  draftUnit: "",
  scope: "",
  level: "",
  normativeDocuments: "",
  publicationDate: "",
  implementationDate: "",
  image: "",
  imageList: props.standardData?.image
    ? props.standardData.image.split(",")
    : [],
  file: props.standardData?.file || "", // 新增文件地址
  fileName: props.standardData?.fileName || "", // 新增文件名
});

const standardNodes = ref<any[]>([]);

// 解析图片链接的方法
const parseImageList = (imageStr: string) => {
  if (!imageStr) return [];
  // 直接按逗号分割并过滤空值
  return imageStr
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);
};

// 数据加载方法
const loadData = async () => {
  try {
    loading.value = true;
    error.value = false;

    // 确保ID类型正确
    const id =
      typeof route.query.id === "string"
        ? Number(route.query.id)
        : Number(route.query.id?.[0]) || 0;

    // 调用API获取数据
    const response = await getStandardById(id);

    // 检查响应码（根据实际API响应结构调整）
    if (response.code !== 1) {
      throw new Error(response.msg || "请求失败");
    }

    // 直接使用已解析的数据
    const data: StandardDetail = response.data || {};

    // 提取图片链接
    standard.value = {
      ...data.standard,
      imageList: parseImageList(data.standard.image),
      file: data.file || "", // 新增文件地址
      fileName: data.fileName || "", // 新增文件名
    };
    // 处理术语图片
    terms.value =
      (data.terms || []).map((term) => ({
        ...term,
        imageList: parseImageList(term.image || ""),
      })) || [];

    // 处理层级数据（添加空值保护）
    const rawLevelData = data.level1 || [];
    standardNodes.value = normalizeStandardData(rawLevelData);
  } catch (err) {
    error.value = true;
    console.error("加载失败:", err);
    ElMessage.error("加载失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};

// 初始化加载
onMounted(loadData);
</script>

<style scoped>
.standard-container {
  display: grid;
  grid-template-columns: 300px 1fr; /* 左侧300px，右侧自适应 */
  grid-template-rows: 100vh; /* 高度为视口高度 */
  overflow: hidden;
}

.nav-sidebar {
  /* position: sticky; */
  top: 0;
  height: 100vh;
  overflow-y: auto;
  padding: 20px;
  border-right: 1px solid #eee;
}

.content-main {
  flex: 1; /* 占据剩余宽度 */
  height: 100%; /* 高度与父容器一致 */
  overflow-y: auto; /* 如果内容超出，显示滚动条 */
  padding: 16px; /* 内容区域内边距 */
  background-color: #ffffff; /* 内容区域背景色 */
}

.standard-header {
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.meta-info {
  margin-top: 15px;
  color: #666;

  :is(span) {
    margin-right: 20px;
  }
}

.image-preview-list {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
}
</style>
